// i18n configuration for Ochtopus Website
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import tr from './locales/tr.json';

// Supported languages
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flagCode: 'gb' as const },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flagCode: 'tr' as const },
] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]['code'];

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// Language detection from browser/localStorage
function detectLanguage(): SupportedLanguage {
  // Check localStorage first
  const stored = localStorage.getItem('ochtopus-website-language');
  if (stored && SUPPORTED_LANGUAGES.some(lang => lang.code === stored)) {
    return stored as SupportedLanguage;
  }

  // Check browser language
  const browserLang = navigator.language.split('-')[0];
  if (SUPPORTED_LANGUAGES.some(lang => lang.code === browserLang)) {
    return browserLang as SupportedLanguage;
  }

  // Fallback to default
  return DEFAULT_LANGUAGE;
}

// Get initial language
const initialLanguage = detectLanguage();

// Set document language on load
if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLanguage;
}

// Initialize i18n
i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      tr: { translation: tr },
    },
    lng: initialLanguage,
    fallbackLng: DEFAULT_LANGUAGE,
    debug: import.meta.env.DEV,

    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: false,
    },
  });

// Change language and persist
export function changeLanguage(lang: SupportedLanguage) {
  i18n.changeLanguage(lang);
  localStorage.setItem('ochtopus-website-language', lang);
  document.documentElement.lang = lang;
}

// Get current language info
export function getCurrentLanguage() {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === i18n.language) || SUPPORTED_LANGUAGES[0];
}

export default i18n;
